import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { SUPPORTED_LOCALES, SupportedLocale } from '../i18n';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  canonicalUrl?: string;
  ogImage?: string;
  ogType?: string;
  structuredData?: Record<string, unknown> | Record<string, unknown>[];
  noIndex?: boolean;
}

const SEOHead = ({
  title = "cSquare GRC | Multi-Framework Compliance Platform",
  description = "The compliance platform for organizations whose obligations outgrew SOC 2. One control set carries DORA, NIS2, the EU AI Act, FedRAMP and Single Audit.",
  keywords = "multi-framework compliance, DORA compliance, NIS2, EU AI Act, FedRAMP, HITRUST, BSA/AML, Single Audit, 2 CFR 200, control mapping, continuous controls monitoring, regulatory intelligence, GRC platform",
  canonicalUrl,
  ogImage = "https://csquaregrc.com/csquare-og.png",
  ogType = "website",
  structuredData,
  noIndex = false
}: SEOHeadProps) => {
  const location = useLocation();

  // Extract locale from pathname
  const getLocaleFromPath = (): SupportedLocale => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const potentialLocale = pathSegments[0] as SupportedLocale;
    return SUPPORTED_LOCALES.includes(potentialLocale) ? potentialLocale : 'en-US';
  };

  const currentLocale = getLocaleFromPath();
  const pathWithoutLocale = location.pathname.replace(/^\/[a-z]{2}-[A-Z]{2}/, '') || '/';
  const baseUrl = 'https://csquaregrc.com';
  const currentUrl = canonicalUrl || `${baseUrl}/${currentLocale}${pathWithoutLocale}`;

  useEffect(() => {
    // Update document title
    document.title = title;

    // Set HTML lang attribute  
    document.documentElement.lang = currentLocale;

    // Update meta tags
    const updateMeta = (name: string, content: string, property = false) => {
      const selector = property ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      let meta = document.querySelector(selector) as HTMLMetaElement;
      if (!meta) {
        meta = document.createElement('meta');
        if (property) {
          meta.setAttribute('property', name);
        } else {
          meta.setAttribute('name', name);
        }
        document.head.appendChild(meta);
      }
      meta.content = content;
    };

    // Update link tags
    const updateLink = (rel: string, href: string, type?: string, title?: string, hreflang?: string) => {
      let selector = `link[rel="${rel}"]`;
      if (hreflang) {
        selector = `link[rel="${rel}"][hreflang="${hreflang}"]`;
      }
      let link = document.querySelector(selector) as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = rel;
        if (hreflang) link.hreflang = hreflang;
        document.head.appendChild(link);
      }
      link.href = href;
      if (type) link.type = type;
      if (title) link.title = title;
    };

    // Remove existing hreflang and canonical links
    const existingLinks = document.querySelectorAll('link[rel="canonical"], link[rel="alternate"][hreflang]');
    existingLinks.forEach(link => link.remove());

    // Basic meta tags
    updateMeta('description', description);
    updateMeta('keywords', keywords);
    updateMeta('robots', noIndex ? 'noindex, follow' : 'index, follow');
    updateMeta('author', 'cSquare GRC');
    updateMeta('viewport', 'width=device-width, initial-scale=1.0');
    updateMeta('theme-color', '#1a365d');
    
    // Business/Professional meta tags with locale support
    const GEO_COUNTRY: Record<string, string> = {
      'en-US': 'United States',
      'fr-FR': 'France',
      'es-MX': 'Mexico',
      'de-DE': 'Germany',
      'ja-JP': 'Japan',
    };
    updateMeta('company', 'cSquare GRC');
    updateMeta('geo.region', currentLocale.split('-')[1] || 'US');
    updateMeta('geo.country', GEO_COUNTRY[currentLocale] || 'United States');
    updateMeta('content-language', currentLocale);
    updateMeta('rating', 'General');
    updateMeta('revisit-after', '7 days');

    // Open Graph meta tags with locale support
    updateMeta('og:type', ogType, true);
    updateMeta('og:url', currentUrl, true);
    updateMeta('og:title', title, true);
    updateMeta('og:description', description, true);
    updateMeta('og:image', ogImage, true);
    updateMeta('og:image:width', '1200', true);
    updateMeta('og:image:height', '630', true);
    updateMeta('og:image:alt', 'cSquare GRC - Multi-Agent AI for Continuous Compliance', true);
    updateMeta('og:site_name', 'cSquare GRC', true);
    updateMeta('og:locale', currentLocale.replace('-', '_'), true);

    // Remove existing og:locale:alternate tags
    const existingAlternateLocales = document.querySelectorAll('meta[property="og:locale:alternate"]');
    existingAlternateLocales.forEach(meta => meta.remove());

    // Add og:locale:alternate for other supported locales
    SUPPORTED_LOCALES.filter(locale => locale !== currentLocale).forEach(locale => {
      updateMeta(`og:locale:alternate`, locale.replace('-', '_'), true);
    });

    updateMeta('fb:app_id', '', true); // Add if you have a Facebook app

    // Twitter meta tags
    updateMeta('twitter:card', 'summary_large_image');
    updateMeta('twitter:site', '@cSquareAI');
    updateMeta('twitter:creator', '@cSquareAI');
    updateMeta('twitter:url', currentUrl);
    updateMeta('twitter:title', title);
    updateMeta('twitter:description', description);
    updateMeta('twitter:image', ogImage);
    updateMeta('twitter:image:alt', 'cSquare GRC - Multi-Framework Compliance Platform');

    // Canonical URL
    updateLink('canonical', currentUrl);

    // Hreflang alternates for all supported locales
    SUPPORTED_LOCALES.forEach(locale => {
      const hreflangUrl = `${baseUrl}/${locale}${pathWithoutLocale}`;
      updateLink('alternate', hreflangUrl, undefined, undefined, locale);
    });

    // x-default hreflang
    updateLink('alternate', `${baseUrl}/en-US${pathWithoutLocale}`, undefined, undefined, 'x-default');
    
    // RSS Feed Auto-Discovery
    updateLink('alternate', 'https://csquaregrc.com/feed.xml', 'application/rss+xml', 'c² Insights');

    // Structured data with locale support
    // Always clear previously injected/static tagged JSON-LD so leaf pages
    // do not inherit the sitewide block.
    document
      .querySelectorAll('script[type="application/ld+json"][data-seo="true"]')
      .forEach(script => script.remove());

    if (structuredData) {

      // Handle single object or array of schemas
      const schemas = Array.isArray(structuredData) ? structuredData : [structuredData];
      
      schemas.forEach((schema, index) => {
        const enhancedSchema = {
          ...schema,
          inLanguage: currentLocale,
          ...(schema['@type'] === 'WebSite' && {
            potentialAction: {
              ...(schema.potentialAction as Record<string, unknown> || {}),
              target: `${baseUrl}/{search_term_string}`,
              'query-input': 'required name=search_term_string'
            }
          })
        };

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.setAttribute('data-seo', 'true');
        script.setAttribute('data-index', String(index));
        script.textContent = JSON.stringify(enhancedSchema);
        document.head.appendChild(script);
      });
    }
  }, [title, description, keywords, currentUrl, ogImage, ogType, structuredData, currentLocale, pathWithoutLocale, noIndex]);

  return null;
};

export default SEOHead;